import { api, authClient } from "@repo/db"
import { createFileRoute } from "@tanstack/react-router"

import { convexClient, queryWithTimeout } from "@/lib/convex"

export const Route = createFileRoute("/dealerships")({
  beforeLoad: async (_ctx) => {
    // const session = await authClient.getSession()
    // if (!session || !session.data) {
    //   return (window.location.href = '/login')
    // }

    const currentUser = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (!currentUser) {
      return (window.location.href = "/login")
    }
    if (!currentUser.name || !currentUser.email) {
      return (window.location.href = "/complete-profile")
    }
    if (currentUser.isDemo && !currentUser.dealership) {
      return (window.location.href = "/demo")
    }

    // First check if user has an active dealership assigned
    try {
      const userDealership = await convexClient.mutation(
        api.users.mutations.getCurrentDealership,
        {},
      )

      if (userDealership) {
        // User already has a dealership assigned, redirect to dashboard
        return (window.location.href = `/${userDealership.slug}/dashboard`)
      }
    } catch {
      console.log("No dealership assigned to user yet")
    }

    // No dealership found, redirect to create one
    return (window.location.href = "/create-dealership")
  },

  component: Dealerships,
})

function Dealerships() {
  const { data: session } = authClient.useSession()
  const { data: organizationId } = authClient.useListOrganizations()

  if (organizationId) {
    return (
      <div className="space-y-6">
        <pre>{JSON.stringify(session, null, 2)}</pre>
        <pre>{JSON.stringify(organizationId, null, 2)}</pre>
        <h2 className="text-2xl font-bold">Organization</h2>
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground">
            You need to be part of an organization to view this page.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Organization Details</h2>
          <p className="text-muted-foreground">Manage your dealership organization settings</p>
        </div>
        {/* Show edit button for owners */}
        {session && (
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Edit Organization
          </button>
        )}
      </div>

      <div className="grid gap-6">
        <div className="rounded-lg border p-6">
          <h3 className="mb-4 text-lg font-medium">Organization Information</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">Premium Motors Group</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Role</p>
              <p className="font-medium capitalize">Owner</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium">{new Date().toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Members</p>
              <p className="font-medium">-</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border p-6">
          <h3 className="mb-4 text-lg font-medium">Team Members</h3>
          <div className="mb-4 flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Manage who has access to your organization
            </p>
            {/* Show invite button for owners/admins */}
            {session && (
              <button
                type="button"
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
              >
                Invite Member
              </button>
            )}
          </div>
          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-4 py-3 text-sm">John Smith</td>
                  <td className="px-4 py-3 text-sm">{session?.user.email}</td>
                  <td className="px-4 py-3 text-sm capitalize">Owner</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold text-green-800">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="text-muted-foreground">You</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border p-6">
          <h3 className="mb-4 text-lg font-medium">Organization Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Subdomain</p>
                <p className="text-sm text-muted-foreground">Your dealership's unique subdomain</p>
              </div>
              <button type="button" className="text-primary hover:underline text-sm">
                Configure
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Branding</p>
                <p className="text-sm text-muted-foreground">Logo, colors, and theme settings</p>
              </div>
              <button type="button" className="text-primary hover:underline text-sm">
                Customize
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Features</p>
                <p className="text-sm text-muted-foreground">Enable or disable platform features</p>
              </div>
              <button type="button" className="text-primary hover:underline text-sm">
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
